import { Button } from '@ab-inbev-maz/compass';
import Tableview from '../../components/commos/Tableview'
import NavigationHeader from '../../components/navigation/NavigationHeader';
import Loading from '../../features/sso-auth/components/Loading';
import type { ReportListItem } from '../../services/reportsService';
import { useReportsStore } from '../../stores/reportsStore';
import { useEffect, useState } from 'react';
import SimpleErrorGroupsTable from '../../components/errors/SimpleErrorGroupsTable';

// Subcomponente para respetar las reglas de hooks y simplificar el render de la columna
const ReprocessButtonCell = ({ row, disabled }: { row: any; disabled: boolean }) => {
  const processComboWithError = useReportsStore((state) => state.processComboWithError);
  const comboId = row?.combo_id ?? row?.data?.combo_id;

  const handleClick = async () => {
    try {
      const idToSend = comboId ? String(comboId) : "";
      await processComboWithError([idToSend]);
    } catch (error) {
      console.error('Error al reprocesar combo:', error);
    }
  };

  return (
    <Button
      className='border-none cursor-pointer'
      icon={'arrow/repeat'}
      size="sm"
      variant="secondary"
      onClick={handleClick}
      disabled={disabled}
    />
  );
};

const TablePage = () => {
  const [activeTab, setActiveTab] = useState<'individual' | 'groups'>('individual');
  const [datos, setDatos] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const globalLoading = useReportsStore((state) => state.loading);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const getListReports = useReportsStore((state) => state.getListReports);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await getListReports({
          page: currentPage,
          page_size: pageSize,
          search: debouncedSearch || undefined
        });
        setDatos(response.results);
        setTotalCount(response.count);
      } catch (err) {
        console.error('Error fetching Reports:', err);
      } finally {
        setLoading(false);
      }
    };
    void fetchReports();
  }, [currentPage, pageSize, debouncedSearch, getListReports]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const isRowDisabled = (row: any) => {
    if (row.data) {
      return row.data.reprocessable === false;
    }
    return row.reprocessable === false;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const columnas = [
    { key: 'sku', title: 'SKU' },
    { key: 'combo_id', title: 'Combo ID' },
    { key: 'created_at', title: 'Fecha de creación', render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'timestamp', title: 'Timestamp', render: (_: any, row: any) => (
      <span>{row.timestamp ?? ''}</span>
    ) },
    { key: 'error_code', title: 'Motivo del error' },
    { key: 'error_message', title: <span data-testid="column-message">Mensaje</span>, render: (value: string) => (
      <div className='max-w-[500px] text-center'>{value ?? ''}</div>
    ) },
    {
      key: 'level',
      title: 'Level',
      render: (_: any, row: any) => (
        <span data-testid="column-level">{row.level ?? ''}</span>
      ),
    },
    {
      key: 'reprocessable',
      title: 'Reprocesable',
      render: (_: any, row: any) => (
        <ReprocessButtonCell row={row} disabled={isRowDisabled(row)} />
      ),
    },
  ];





  if (loading || globalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos cargando los errores." />
        </div>
      </div>
    );
  }

  return (
    <>
      <NavigationHeader
        title='Errores'
        description='Desde aquí puedes consultar todos los errores registrados en el sistema.'
        backLink='/'
      />

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4 px-6">
          <button
            onClick={() => setActiveTab('individual')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'individual'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Errores Individuales
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'groups'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Grupos de Errores
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'individual' ? (
        <Tableview
          title='Errores Individuales'
          datos={datos}
          columnas={columnas}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          showReprocessButton={true}
          rowDisabled={isRowDisabled}
        />
      ) : (
        <SimpleErrorGroupsTable />
      )}
    </>
  )
}

export default TablePage

